import {setCookie, getCookie} from 'react-use-cookie'
import { ApiRequest } from '../../api/api'
import moment from 'moment'

// Token刷新相关常量
const TOKEN_REFRESH_THRESHOLD = 2 * 60 * 1000 // 提前2分钟刷新token
const CREDENTIALS_KEY = 'user_credentials'
const TOKEN_EXPIRES_AT_KEY = 'token_expires_at'

// 防止token刷新重复尝试的标志
let isRefreshingToken = false
let lastRefreshAttempt = 0
const REFRESH_COOLDOWN = 30000 // 30秒冷却时间，防止频繁刷新尝试

// Tracks whether the session was ended (logout / 401 cleanup).
// An in-flight token refresh must never resurrect a logged-out session:
// its response arrives after logout cleared the cookies, re-sets the token
// cookie, and the user gets bounced back into the app with an empty menu.
let sessionEnded = false

export const authenticated = () => {
  const token = getCookie('token')
  if (token && token.length) {
    return true
  } else {
    return false
  }
}

export const getUserId = () => {
  return parseInt(getCookie('id'), 10)
}

export const getUserName = () => {
  return getCookie('userName')
}

export const getCustomerUserId = () => {
  return getCookie('customerUserId')
}

/**
 * antd Upload 组件用自带 XHR 发请求，不会经过 api-services.js 的 axios
 * 拦截器（token 是在那里注入的），所以带 action 的 Upload 必须手动
 * 传入 headers，否则后端返回 401。
 */
export const getAuthHeaders = (): { Authorization?: string } => {
  const token = getCookie('token')
  return token && token.length ? { Authorization: `Bearer ${token}` } : {}
}

export const getName = () => {
  return getCookie('name')
}

/**
 * 检查token是否即将过期
 * @returns {boolean} 如果token在5分钟内过期返回true
 */
export const shouldRefreshToken = (): boolean => {
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_AT_KEY)
  if (!expiresAt) return false

  try {
    // localStorage中已经存储的是本地时间，直接比较
    const expiresAtMoment = moment(expiresAt)
    const nowMoment = moment()

    // 检查日期是否有效
    if (!expiresAtMoment.isValid()) {
      console.error('Invalid expiresAt date:', expiresAt)
      return false
    }

    // 计算时间差（毫秒）
    const timeUntilExpiry = expiresAtMoment.diff(nowMoment)
    const timeUntilExpiryMinutes = timeUntilExpiry / 1000 / 60

    console.log('⏰ Time comparison (Local):', {
      expiresAtLocal: expiresAtMoment.format('YYYY-MM-DD HH:mm:ss'),
      nowLocal: nowMoment.format('YYYY-MM-DD HH:mm:ss'),
      timeUntilExpiryMinutes: timeUntilExpiryMinutes.toFixed(2),
      thresholdMinutes: TOKEN_REFRESH_THRESHOLD / 1000 / 60,
      shouldRefresh: timeUntilExpiry < TOKEN_REFRESH_THRESHOLD
    })

    return timeUntilExpiry < TOKEN_REFRESH_THRESHOLD
  } catch (error) {
    console.error('Error checking token expiration:', error)
    return false
  }
}

/**
 * 获取存储的用户凭据（用于刷新token）
 * @returns {{userName: string, password: string} | null}
 */
export const getStoredCredentials = (): { userName: string; password: string } | null => {
  try {
    const credentials = localStorage.getItem(CREDENTIALS_KEY)
    if (!credentials) return null

    // 简单的Base64解码（生产环境建议使用更安全的加密方式）
    const decoded = atob(credentials)
    return JSON.parse(decoded)
  } catch (error) {
    console.error('Error getting stored credentials:', error)
    return null
  }
}

/**
 * 存储用户凭据（用于刷新token）
 * @param userName 用户名
 * @param password 密码
 */
export const storeCredentials = (userName: string, password: string) => {
  try {
    const credentials = { userName, password }
    // 简单的Base64编码（生产环境建议使用更安全的加密方式）
    const encoded = btoa(JSON.stringify(credentials))
    localStorage.setItem(CREDENTIALS_KEY, encoded)
    console.log('💾 User credentials stored for token refresh')
  } catch (error) {
    console.error('Error storing credentials:', error)
  }
}

/**
 * 刷新token
 * @returns {Promise<{token: string, expiresAt: string} | null>}
 */
export const refreshToken = async (): Promise<{ token: string; expiresAt: string } | null> => {
  try {
    const credentials = getStoredCredentials()
    if (!credentials) {
      console.log('❌ No stored credentials found for token refresh')
      return null
    }

    console.log('🔄 Attempting token refresh with credentials:', {
      hasUserName: !!credentials.userName,
      userNamePreview: credentials.userName ? credentials.userName.substring(0, 3) + '***' : 'undefined',
      hasPassword: !!credentials.password
    })

    const response = await ApiRequest({
      url: 'Employee/RefreshToken',
      method: 'post',
      data: {
        UserName: credentials.userName,
        Password: credentials.password
      },
      isShowSpinner: false // 不显示加载动画
    })

    console.log('🔄 Token refresh API response:', response ? 'Success' : 'Failed')

    if (response?.data?.data) {
      // Session was ended while this refresh was in flight — drop the result.
      // (logout()/401 cleanup cleared the token cookie; restoring it here
      // would silently log the user back in with a broken, menu-less session)
      if (sessionEnded || !getCookie('token')) {
        console.log('⚠️ Token refresh result ignored (session ended while refreshing)')
        return null
      }

      const newToken = response.data.data.token
      const newExpiresAt = response.data.data.expiresAt

      // 更新token
      setCookie('token', newToken, { days: 1 })

      // 将UTC时间转换为本地时间后存储
      if (newExpiresAt) {
        const localExpiresAt = moment.utc(newExpiresAt).local().format('YYYY-MM-DDTHH:mm:ss')
        localStorage.setItem(TOKEN_EXPIRES_AT_KEY, localExpiresAt)
        console.log('Token refreshed and saved with local expiration time:', localExpiresAt)
      }

      return { token: newToken, expiresAt: newExpiresAt }
    }

    return null
  } catch (error) {
    console.error('Error refreshing token:', error)
    return null
  }
}

/**
 * 自动刷新token（如果需要）
 * @returns {Promise<boolean>} 是否成功刷新
 */
export const autoRefreshTokenIfNeeded = async (): Promise<boolean> => {
  // Session already ended — nothing to refresh
  if (sessionEnded || !getCookie('token')) {
    return false
  }

  // 防止重复刷新尝试
  const now = Date.now()
  if (isRefreshingToken || (now - lastRefreshAttempt) < REFRESH_COOLDOWN) {
    console.log('⏸️ Token refresh skipped (already refreshing or in cooldown)')
    return false
  }

  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_AT_KEY)

  // 调试日志：显示token状态（使用本地时间）
  console.log('🔍 Token Check:', {
    hasExpiresAt: !!expiresAt,
    expiresAtLocal: expiresAt ? moment(expiresAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A',
    nowLocal: moment().format('YYYY-MM-DD HH:mm:ss'),
    shouldRefresh: shouldRefreshToken(),
    timeUntilExpiryMinutes: expiresAt ?
      moment(expiresAt).diff(moment(), 'minutes').toFixed(2) : 'N/A'
  })

  if (!shouldRefreshToken()) {
    return true // Token不需要刷新
  }

  // 设置刷新标志和最后尝试时间
  isRefreshingToken = true
  lastRefreshAttempt = now

  console.log('🔄 Token is expiring soon, attempting to refresh...')
  const result = await refreshToken()

  // 重置刷新标志
  isRefreshingToken = false

  if (result) {
    console.log('✅ Token refreshed successfully:', {
      newTokenPreview: result.token ? `${result.token.substring(0, 20)}...` : 'NO_TOKEN',
      newExpiresAtUTC: result.expiresAt,
      newExpiresAtLocal: localStorage.getItem(TOKEN_EXPIRES_AT_KEY)
    })
    return true
  } else {
    console.warn('❌ Token refresh failed')
    return false
  }
}

export const login = (data:any, callback:any) => {
  console.log(data)
  sessionEnded = false
  localStorage.setItem('pages', JSON.stringify(data.pages))

  // 将UTC时间转换为本地时间后存储
  if (data.expiresAt) {
    const localExpiresAt = moment.utc(data.expiresAt).local().format('YYYY-MM-DDTHH:mm:ss')
    localStorage.setItem(TOKEN_EXPIRES_AT_KEY, localExpiresAt)
    console.log('Token expires at (UTC):', data.expiresAt)
    console.log('Token expires at (Local):', localExpiresAt)
  }

  setCookie('token', data.token, { days: 1 })
  setCookie('userName', data.userName)
  setCookie('name', data.name || (data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : ''))
  setCookie('email', data.email || '')
  setCookie('id', data.employeeId)

  callback()
}

export const loginCustomer = (data:any, callback:any) => {
  console.log(data)
  sessionEnded = false

  // The customer portal calls the same secured APIs as staff pages
  // (DeliveryMethod/GetAllDeliveryMethod, SalesOrder/GetByCust, ...).
  // Without a Bearer token every one of them answers 401 and the global
  // 401 handler kicks the customer back to the login page, so the token
  // returned by OnlineUser/OnlineUserLogin MUST be stored here.
  if (data.token) {
    setCookie('token', data.token, { days: 1 })
  } else {
    console.error('OnlineUserLogin did not return a token - customer APIs will fail with 401')
  }

  if (data.expiresAt) {
    const localExpiresAt = moment.utc(data.expiresAt).local().format('YYYY-MM-DDTHH:mm:ss')
    localStorage.setItem(TOKEN_EXPIRES_AT_KEY, localExpiresAt)
  }

  setCookie('customerUserId', data.customerId)
  callback()
}

export const logoutCustomer = (callback: any) => {
  sessionEnded = true
  callback()
  setCookie('customerUserId', '')
  setCookie('token', '')
  localStorage.removeItem(TOKEN_EXPIRES_AT_KEY)
  localStorage.removeItem(CREDENTIALS_KEY)
}

export const logout = (callback: any) => {
  sessionEnded = true
  callback()
  localStorage.removeItem('pages')
  localStorage.removeItem(CREDENTIALS_KEY)
  localStorage.removeItem(TOKEN_EXPIRES_AT_KEY)
  setCookie('token', '')
  setCookie('email', '')
  setCookie('userName', '')
  setCookie('name', '')
}
